import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const TIER_COLORS: Record<string, string> = {
  free: 'bg-ll-muted/20 text-ll-muted',
  pro: 'bg-blue-500/20 text-blue-400',
  team: 'bg-purple-500/20 text-purple-400',
  enterprise: 'bg-amber-500/20 text-amber-400',
};

export function UserMenu() {
  const { user, profile, isAdmin, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const email = user?.email ?? '';
  const initials = email.slice(0, 2).toUpperCase();
  const tier = profile?.tier ?? 'free';

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-sm text-ll-muted hover:text-ll-text transition-colors"
      >
        <span className="w-7 h-7 rounded-full bg-ll-cyan/20 text-ll-cyan flex items-center justify-center text-xs font-bold">
          {initials}
        </span>
        <span className="hidden sm:inline max-w-[160px] truncate">{email}</span>
        <span
          className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${TIER_COLORS[tier] ?? TIER_COLORS['free']}`}
        >
          {tier}
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 5l3 3 3-3" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-48 bg-ll-surface border border-ll-border rounded-lg shadow-[0_8px_30px_rgba(0,0,0,0.5)] py-1 z-50">
          <Link
            to="/account"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-ll-muted hover:text-ll-text hover:bg-white/5 transition-colors"
          >
            Account
          </Link>
          <Link
            to="/downloads"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-ll-muted hover:text-ll-text hover:bg-white/5 transition-colors"
          >
            Downloads
          </Link>
          {isAdmin && (
            <Link
              to="/admin"
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-sm text-ll-amber hover:text-ll-amber hover:bg-white/5 transition-colors"
            >
              Admin
            </Link>
          )}
          <div className="border-t border-ll-border my-1" />
          <button
            onClick={async () => {
              setOpen(false);
              await signOut();
              navigate('/sign-in');
            }}
            className="block w-full text-left px-4 py-2 text-sm text-ll-muted hover:text-ll-text hover:bg-white/5 transition-colors"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
